import { useGetAllOfficesQuery } from '@/app/features/control-panel/organization/organization.api';
import { useGetAllDepartmentsQuery } from '@/app/features/employee/emp-dashboard/emp-dashboard.api';
import {
  useGetPayrollLeakageAndLostWorkingHoursQuery,
  useGetTodayAttendanceHealthQuery,
} from '@/app/features/employee/management-dashboard/management-dashboard.api';
import CustomCircleChart from '@/components/Chart/CustomCircleChart';
import useDiscloser from '@/hooks/useDiscloser';
import useError from '@/hooks/useError';
import useManagementFilters from '@/hooks/useManagementFilters';
import { useAppDiscloser, useSelectionOptions } from '@/hooks';
import { Calendar03Icon, ChevronDownIcon, CirclePlusIcon, DownloadIcon } from '@/icons';
import { formatDateRange } from '@/utils';
import { Button, DatePicker, Flex, Select } from 'antd';
import dayjs from 'dayjs';
import BreakTimeAbuse from './_components/ManagementDashboard/BreakTimeAbuse';
import CostPerProductiveHours from './_components/ManagementDashboard/CostPerProductiveHours';
import DisciplineTrend from './_components/ManagementDashboard/DisciplineTrend';
import EarlyComerRatio from './_components/ManagementDashboard/EarlyComerRatio';
import GraceTimeAbuse from './_components/ManagementDashboard/GraceTimeAbuse';
import GraceTimeAbuseIndex from './_components/ManagementDashboard/GraceTimeAbuseIndex';
import HighSalaryLowOutput from './_components/ManagementDashboard/HighSalaryLowOutput';
import LateGoerRatio from './_components/ManagementDashboard/LateGoerRatio';
import LostWorkHours from './_components/ManagementDashboard/LostWorkHours';
import OfficeHour from './_components/ManagementDashboard/OfficeHour';
import ReportDownloadModal from './_components/ManagementDashboard/ReportDownloadModal';
import Top10DisciplineIssues from './_components/ManagementDashboard/Top10DisciplineIssues';
import WorkDisciplineScore from './_components/ManagementDashboard/WorkDisciplineScore';

const { RangePicker } = DatePicker;

const ManagementDashboard = () => {
  const { filter, setFilter, isFilterChanged, resetFilter } =
    useManagementFilters();
  const payrollFilter = filter.payrollLeakageLostWorkHour;
  const workForceCultureFilter = filter.workForceCulture;
  const [config, handleOpen, handleClose] = useDiscloser();
  const [, openCreateEmployee] = useAppDiscloser('CREATE_EMPLOYEE');

  // Get Attendance Health
  const { data: attendanceHealthData, error: attendanceHealthError } =
    useGetTodayAttendanceHealthQuery({
      department_id: filter.department_id,
      office_id: filter.office_id,
    });
  useError(attendanceHealthError, 'Failed to fetch attendance health');

  // Get Payroll Leakage and Lost Working Hours
  const { data: leakageData, error: leakageError } =
    useGetPayrollLeakageAndLostWorkingHoursQuery({
      start_date: payrollFilter.start_date || '',
      end_date: payrollFilter.end_date || '',
      department_id: filter.department_id,
      office_id: filter.office_id,
    });
  useError(leakageError, 'Failed to fetch payroll leakage');

  // Get Payroll Leakage and Lost Working Hours Summary
  const payrollSummary = leakageData?.data?.data?.summary;
  const attendanceHealth = attendanceHealthData?.data?.data;

  // Get Department Options
  const [departmentOptions] = useSelectionOptions(useGetAllDepartmentsQuery, {
    labelKey: 'name',
    valueKey: 'id',
    params: { params: { limit: 100 } },
  });

  // Get Office Options
  const [officeOptions] = useSelectionOptions(useGetAllOfficesQuery, {
    labelKey: 'name',
    valueKey: 'id',
    params: { params: { take: 100 } },
  });

  return (
    <div className="space-y-6">
      {/* Filters */}
      <Flex
        align="center"
        justify="space-between"
        className="dark:bg-theme-slate-800 border dark:border-theme-gray-700 p-3 rounded-xl w-full"
      >
        <Flex align="center" gap={16} className="input-bg-transparent">
          <Select
            allowClear
            size="large"
            placeholder="Department"
            options={departmentOptions}
            value={filter.department_id}
            onChange={(val) => setFilter('department_id', val)}
            suffixIcon={
              <ChevronDownIcon className="text-white size-6" size={24} />
            }
          />
          <Select
            allowClear
            size="large"
            placeholder="Branch"
            options={officeOptions}
            value={filter.office_id}
            onChange={(val) => setFilter('office_id', val)}
            suffixIcon={
              <ChevronDownIcon className="text-white size-6" size={24} />
            }
          />
        </Flex>
        <Flex align="center" gap={16} className="input-bg-transparent">
          <Button
            size="large"
            className="btn-primary"
            onClick={() => openCreateEmployee({ type: 'ADD' })}
          >
            <CirclePlusIcon size={20} />
            Add New Employee
          </Button>
          <Button
            size="large"
            className={'btn-primary'}
            onClick={() => handleOpen({})}
          >
            Export
            <DownloadIcon size={16} strokeWidth={2.5} />
          </Button>
        </Flex>
      </Flex>

      {/* Owner Controls */}
      <div className="bg-theme-slate-800 border border-theme-gray-700 p-6 rounded-xl w-full">
        <Flex
          align="center"
          justify="space-between"
          className="bg-theme-slate-800 border-b border-b-theme-gray-700 pb-4 mb-4 w-full"
        >
          <h2 className="uppercase text-2xl text-white font-bold">
            Owner Control
          </h2>
        </Flex>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <WorkDisciplineScore
            department_id={filter.department_id}
            office_id={filter.office_id}
            filter={filter}
            setFilter={setFilter}
            isFilterChanged={isFilterChanged}
            resetFilter={resetFilter}
          />
          <div className="bg-theme-slate-900 border border-theme-gray-700 p-4 rounded-xl w-full space-y-4">
            <Flex justify="center">
              <h4 className="text-lg px-3 py-1 rounded border border-theme-gray-700 text-white text-center leading-tight">
                Attendance Health{' '}
                <span className="text-theme-primary">(%)</span>
              </h4>
            </Flex>
            <CustomCircleChart
              value={attendanceHealth?.attendance_percentage || 0}
              color="#00C6AE"
              label="Today"
            />
          </div>

          <div className="bg-theme-slate-900 border border-theme-gray-700 p-4 rounded-xl w-full space-y-4">
            <Flex justify="center">
              <h4 className="text-lg px-3 py-1 rounded border border-theme-gray-700 text-white text-center leading-tight">
                Late Attendance Health{' '}
                <span className="text-theme-primary">(%)</span>
              </h4>
            </Flex>
            <CustomCircleChart
              value={attendanceHealth?.late_percentage || 0}
              color="#2CA2C1"
              label="Today"
            />
          </div>
          <div className="col-span-1 md:col-span-2">
            <OfficeHour
              department_id={filter.department_id}
              office_id={filter.office_id}
              filter={filter}
              setFilter={setFilter}
              isFilterChanged={isFilterChanged}
              resetFilter={resetFilter}
            />
          </div>
          <Top10DisciplineIssues
            department_id={filter.department_id}
            office_id={filter.office_id}
            filter={filter}
            setFilter={setFilter}
            isFilterChanged={isFilterChanged}
            resetFilter={resetFilter}
          />
        </div>
      </div>

      {/* Payroll Leakage & Cost Efficiency */}
      <div className="bg-theme-slate-800 border border-theme-gray-700 p-6 rounded-xl w-full">
        <Flex
          align="center"
          justify="space-between"
          className="bg-theme-slate-800 border-b border-b-theme-gray-700 pb-4 mb-4 w-full"
        >
          <h2 className="uppercase text-2xl text-white font-bold">
            PAYROLL LEAKAGE & COST EFFICIENCY
          </h2>
          <Flex align="center" gap={16} className="input-bg-transparent">
            <RangePicker
              size="large"
              suffixIcon={null}
              allowClear={false}
              style={{ width: 255 }}
              prefix={<Calendar03Icon className="size-5" />}
              className="!bg-transparent"
              value={[
                payrollFilter?.start_date
                  ? dayjs(payrollFilter.start_date)
                  : null,
                payrollFilter?.end_date ? dayjs(payrollFilter.end_date) : null,
              ]}
              onChange={(_, dateStrings) => {
                setFilter('payrollLeakageLostWorkHour', {
                  start_date: dateStrings[0],
                  end_date: dateStrings[1],
                });
              }}
              disabledDate={(current) =>
                current > dayjs().subtract(1, 'day').endOf('day')
              }
            />
          </Flex>
        </Flex>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <GraceTimeAbuse
            department_id={filter.department_id}
            office_id={filter.office_id}
            filter={filter}
            setFilter={setFilter}
            isFilterChanged={isFilterChanged}
            resetFilter={resetFilter}
          />
          <div className="bg-theme-slate-900 border border-theme-gray-700 p-4 rounded-xl w-full space-y-4 relative">
            <Flex justify="center">
              <h4 className="text-lg px-3 py-1 rounded border border-theme-gray-700 text-white leading-tight text-center">
                Payroll Leakage <span className="text-theme-primary">(%)</span>
              </h4>
            </Flex>

            <CustomCircleChart
              value={payrollSummary?.average_lost_percentage || 0}
              label={formatDateRange(
                payrollFilter.start_date,
                payrollFilter.end_date,
                { showYearIfSame: false },
              )}
            />
          </div>
          <HighSalaryLowOutput
            department_id={filter.department_id}
            office_id={filter.office_id}
            filter={filter}
            setFilter={setFilter}
            isFilterChanged={isFilterChanged}
            resetFilter={resetFilter}
          />
          <div className="col-span-1 md:col-span-2 lg:col-span-3 grid grid-cols-1 lg:grid-cols-2 gap-6">
            <LostWorkHours
              daily_breakdown={leakageData?.data?.data?.daily_breakdown}
              start_date={payrollFilter.start_date || ''}
              end_date={payrollFilter.end_date || ''}
            />
            <CostPerProductiveHours
              department_id={filter.department_id}
              office_id={filter.office_id}
              filter={filter}
              setFilter={setFilter}
              isFilterChanged={isFilterChanged}
              resetFilter={resetFilter}
            />
          </div>
          <div className="col-span-1 md:col-span-2 lg:col-span-3">
            <GraceTimeAbuseIndex
              department_id={filter.department_id}
              office_id={filter.office_id}
              filter={filter}
              setFilter={setFilter}
              isFilterChanged={isFilterChanged}
              resetFilter={resetFilter}
            />
          </div>
        </div>
      </div>

      {/* Workforce Behavior & Culture */}
      <div className="bg-theme-slate-800 border border-theme-gray-700 p-6 rounded-xl w-full">
        <Flex
          align="center"
          justify="space-between"
          className="bg-theme-slate-800 border-b border-b-theme-gray-700 pb-4 mb-4 w-full"
        >
          <h2 className="uppercase text-2xl text-white font-bold">
            WORKFORCE BEHAVIOR & CULTURE
          </h2>

          <RangePicker
            size="large"
            suffixIcon={null}
            allowClear={false}
            style={{ width: 255 }}
            prefix={<Calendar03Icon className="size-5" />}
            className="!bg-transparent"
            value={[
              workForceCultureFilter?.start_date
                ? dayjs(workForceCultureFilter.start_date)
                : null,
              workForceCultureFilter?.end_date
                ? dayjs(workForceCultureFilter.end_date)
                : null,
            ]}
            onChange={(_, dateStrings) => {
              setFilter('workForceCulture', {
                start_date: dateStrings[0],
                end_date: dateStrings[1],
              });
            }}
            disabledDate={(current) =>
              current > dayjs().subtract(1, 'day').endOf('day')
            }
          />
        </Flex>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <EarlyComerRatio
            department_id={filter.department_id}
            office_id={filter.office_id}
            filter={filter}
          />
          <LateGoerRatio
            department_id={filter.department_id}
            office_id={filter.office_id}
            filter={filter}
          />
          <BreakTimeAbuse
            department_id={filter.department_id}
            office_id={filter.office_id}
            filter={filter}
          />
          <DisciplineTrend
            department_id={filter.department_id}
            office_id={filter.office_id}
            filter={filter}
            setFilter={setFilter}
            isFilterChanged={isFilterChanged}
            resetFilter={resetFilter}
          />
        </div>
      </div>
      <ReportDownloadModal
        open={config.open}
        onClose={handleClose}
        filters={filter}
      />
    </div>
  );
};

export default ManagementDashboard;
